import { createRouter, createWebHistory, RouteLocationNormalized } from 'vue-router';
import { routes } from './routes';
import { AppRouteRecordRaw } from './types';
import { FLEET_DRIVER_LOGIN_ACCESS_GROUPS } from '../constants/fleet-driver-login-access-groups';
import { EPricingModel } from '@/constants/sponsor';
import { isSubFleetPartner } from '@/utils/subFleet';
import { getPortalDataEnvironment } from '@/utils/portalEnvironment';
import { storeRedirectAfterLogin } from '@/utils/authRedirect';
import { useNavigationProgress } from '@/composables/useNavigationProgress';
import { isPublicPath as isPublicAnonymousPath } from '@/utils/publicRoutes';
import { accessRecords } from './route-access';

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }
    return { top: 0 };
  },
});

const { start, done } = useNavigationProgress();

// Start the navigation progress bar before any other guard runs.
router.beforeEach(() => {
  start();
});

router.beforeEach((to: RouteLocationNormalized, from: RouteLocationNormalized, next: any) => {
  if (to.meta.title) {
    document.title = to.meta.title as string;
  }

  const userAddDataAsStr = localStorage.getItem('userAddData');
  const pathesNoAuthentication = [
    '/auth/login',
    '/auth/register',
    '/auth/password-setup',
    '/auth/2fa',
  ];
  const pathesRedirectIfAuthenticated = ['/auth/login', '/auth/2fa'];
  // Public co-branded voucher landing pages (/promo/:slug) and the public fleet
  // checkout (/checkout/fleet). Prefix-matched rather than listed one by one,
  // because each campaign adds a new slug and a missing entry here would bounce
  // the visitor to the login screen. Signed-in users are let through too: these
  // pages carry no portal chrome and read nothing from the session.
  const isPublicPath = pathesNoAuthentication.includes(to.path) || isPublicAnonymousPath(to.path);

  if (!isPublicPath && !userAddDataAsStr) {
    storeRedirectAfterLogin(to.fullPath);
    // Plain app-open at the root shows a clean login; keep the banner only for blocked deep-links.
    const query = to.fullPath === '/' ? {} : { unauthorized: 'true', redirect: to.fullPath };
    next({ path: '/auth/login', query });
    return;
  }

  if (isPublicPath) {
    if (pathesRedirectIfAuthenticated.includes(to.path) && userAddDataAsStr) {
      next({ path: '/' });
      return;
    }
    return next();
  }

  const userAddData = userAddDataAsStr ? JSON.parse(userAddDataAsStr) : null;
  //console.log("userAddDataRouter", userAddData);

  // Shared document links (/documents?document=<id>) open a dedicated
  // full-page view: the internal preview for ryd users (who have no
  // partnerSettings and would otherwise hit /unauthorized via
  // requiresPartnerFeature) and the partner-facing document page for
  // everyone else. This must live here: a per-route beforeEnter would never
  // run, because this global guard redirects first.
  if (to.name === 'partnerDocuments') {
    const documentId = to.query.document;
    const isRydInternalUser = userAddData?.userGroup === 'ryd';
    if (documentId) {
      next({
        name: isRydInternalUser ? 'partnerDocumentPreview' : 'partnerDocumentView',
        params: { documentId: String(documentId) },
      });
      return;
    }
    if (isRydInternalUser && userAddData.roles?.includes('partnerAdmin')) {
      next({ name: 'partnerDocumentsAdmin' });
      return;
    }
  }

  // Helper functions to check user's permissions
  const hasRequiredRoles = (route: AppRouteRecordRaw) => {
    const requiredRoles = route.meta.requiredRoles || [];
    // `requiredAnyRole`: at least one of them - the semantics the sidebar applies to `requiredRoles`.
    const anyRoles = route.meta.requiredAnyRole;
    const roles = userAddData.roles || [];
    return (
      requiredRoles.every((role: string) => roles.includes(role)) &&
      (!anyRoles?.length || anyRoles.some((role: string) => roles.includes(role)))
    );
  };

  const hasRequiredPartnerTypes = (route: AppRouteRecordRaw) => {
    const requiredPartnerTypes = route.meta?.requiredPartnerTypes;
    if (!requiredPartnerTypes) return true;
    return requiredPartnerTypes.some((type: string) => userAddData.partnerTypes?.includes(type));
  };

  const isInRequiredUserGroup = (route: AppRouteRecordRaw) => {
    const requiredUserGroup = route.meta?.requiredUserGroup;
    if (!requiredUserGroup) return true;
    return requiredUserGroup.includes(userAddData.userGroup);
  };

  const isNotExcludedPartner = (route: AppRouteRecordRaw) => {
    const excludedPartners = route.meta?.excludedPartners;
    if (!excludedPartners || !userAddData?.userGroup) return true;
    // Mirrors navmenu.vue's isNotExcludedPartner: case-sensitive compare against
    // the lowercase userGroups stored in the menu/route constants.
    // NOTE: the checks below run against the *first* matched route carrying any
    // requirement (parent before child), so a child's excludedPartners is only
    // evaluated while its parents declare none.
    return !excludedPartners.includes(userAddData.userGroup);
  };

  const hasRequiredRydEmailDomain = (route: AppRouteRecordRaw) => {
    if (!route.meta?.requiresRydEmailDomain) return true;

    // Backward compatibility check
    if (
      userAddData?.userGroup &&
      FLEET_DRIVER_LOGIN_ACCESS_GROUPS.includes(userAddData.userGroup)
    ) {
      return true;
    }

    const domain = userAddData?.partnerSettings?.mainSponsor?.partnerProfile?.rydEmailDomain;
    return !!domain;
  };

  const hasRequiredListPrice = (route: AppRouteRecordRaw) => {
    if (!route.meta.requiresListPrice) return true;
    return (
      userAddData.partnerSettings?.mainSponsor?.businessContext?.pricingModel ===
      EPricingModel.LIST_PRICE
    );
  };

  const hasRequiredPartnerFeature = (route: AppRouteRecordRaw) => {
    const requiredFeature = route.meta?.requiresPartnerFeature;
    if (!requiredFeature) return true;
    // Mirrors navmenu.vue's hasRequiredPartnerFeature: partnerSettings is null
    // for ryd-internal users, so this is false for them unless the route opts in.
    const isInternalUser =
      userAddData?.partnerTypes?.includes('RYD_INTERNAL') === true ||
      userAddData?.userGroup === 'ryd';
    if (route.meta?.partnerFeatureOpenForInternalUsers && isInternalUser) return true;
    return userAddData?.partnerSettings?.featureFlags?.[requiredFeature] === true;
  };

  const isNotHiddenForSubFleet = (route: AppRouteRecordRaw) => {
    if (!route.meta?.hiddenForSubFleet) return true;
    return !isSubFleetPartner(userAddData?.partnerSettings);
  };

  const hasRequiredSubFleet = (route: AppRouteRecordRaw) => {
    if (!route.meta?.requiresSubFleet) return true;
    return isSubFleetPartner(userAddData?.partnerSettings);
  };

  const hasRequiredFleetApis = (route: AppRouteRecordRaw) => {
    if (!route.meta?.requiresFleetApis) return true;
    // Canonical signal — same source the navmenu uses. The dedicated
    // /me/fleet-operations-capability endpoint is still fetched at runtime by
    // pages that need the parentFleet, but it is not cached in userAddData.
    return userAddData?.partnerSettings?.useFleetApis === true;
  };

  const hasFleetApisOrSubFleet = (route: AppRouteRecordRaw) => {
    if (!route.meta?.requiresFleetApisOrSubFleet) return true;
    return (
      userAddData?.partnerSettings?.useFleetApis === true ||
      isSubFleetPartner(userAddData?.partnerSettings)
    );
  };

  const isNotHiddenInProduction = (route: AppRouteRecordRaw) => {
    if (!route.meta?.hiddenInProduction) return true;
    // Environment-scoped tooling (review finding F-29, R-33): validation pages
    // must not be reachable when the portal reads production data.
    return getPortalDataEnvironment() !== 'production';
  };

  const hasFleetOpsOrFleetPartner = (route: AppRouteRecordRaw) => {
    if (!route.meta?.requiresFleetOpsOrFleetPartner) return true;
    // Admit fleet-API partners (useFleetApis) AND pure fleet partners
    // (partnerTypes includes FLEET_PARTNER). The page itself dispatches to the
    // correct order-detail view based on the same signals.
    return (
      userAddData?.partnerSettings?.useFleetApis === true ||
      userAddData?.partnerTypes?.includes('FLEET_PARTNER') === true
    );
  };

  // Check for required roles and user groups. Which records are checked lives in route-access.ts,
  // shared with the BMW Pages overview: roles on the first matched record with any requirement,
  // plus on every record that opts in with `strictRoles` (CF-63). Checking every record for all
  // sections locked out Pay Ops users whose child routes list roles meant as "any of" (CF-83).
  const { primary: routeWithRequirements, roleRecords: roleRoutes } = accessRecords(to.matched);

  if (routeWithRequirements) {
    const roleCheckPassed = roleRoutes.every(
      (route) =>
        (!route.meta?.requiredRoles && !route.meta?.requiredAnyRole) ||
        hasRequiredRoles(route as AppRouteRecordRaw),
    );
    const groupCheckPassed =
      !routeWithRequirements.meta?.requiredUserGroup ||
      isInRequiredUserGroup(routeWithRequirements);
    const domainCheckPassed = hasRequiredRydEmailDomain(routeWithRequirements);
    const listPriceCheckPassed = hasRequiredListPrice(routeWithRequirements);
    const partnerTypeCheckPassed = hasRequiredPartnerTypes(routeWithRequirements);
    const excludedPartnerCheckPassed = isNotExcludedPartner(routeWithRequirements);
    const fleetApisCheckPassed = hasRequiredFleetApis(routeWithRequirements);
    const partnerFeatureCheckPassed = hasRequiredPartnerFeature(routeWithRequirements);
    const fleetOpsOrFleetPartnerCheckPassed = hasFleetOpsOrFleetPartner(routeWithRequirements);
    const fleetApisOrSubFleetCheckPassed = hasFleetApisOrSubFleet(routeWithRequirements);
    const subFleetCheckPassed = isNotHiddenForSubFleet(routeWithRequirements);
    const requiresSubFleetCheckPassed = hasRequiredSubFleet(routeWithRequirements);
    const hiddenInProductionCheckPassed = isNotHiddenInProduction(routeWithRequirements);

    if (
      roleCheckPassed &&
      groupCheckPassed &&
      domainCheckPassed &&
      listPriceCheckPassed &&
      partnerTypeCheckPassed &&
      excludedPartnerCheckPassed &&
      fleetApisCheckPassed &&
      partnerFeatureCheckPassed &&
      fleetOpsOrFleetPartnerCheckPassed &&
      fleetApisOrSubFleetCheckPassed &&
      subFleetCheckPassed &&
      requiresSubFleetCheckPassed &&
      hiddenInProductionCheckPassed
    ) {
      next(); // User satisfies role, group and domain requirements
    } else {
      next({ path: '/unauthorized', query: { reason: 'auth' } }); // User does not meet the required criteria
    }
  } else {
    next(); // No specific roles or user group required, proceed
  }
});

// Finish the navigation progress bar once the route (and its lazy chunk) resolves,
// or if navigation fails (e.g. a lazy-chunk load error) so the bar never sticks.
router.afterEach(() => {
  done();
});
router.onError((error: any) => {
  done();
  const pattern = /Failed to fetch dynamically imported module|Importing a module script failed/i;
  if (pattern.test(error?.message || '')) {
    const chunkReloadKey = 'chunk-load-failed-reload';
    const lastReload = sessionStorage.getItem(chunkReloadKey);
    const now = Date.now();
    if (!lastReload || now - parseInt(lastReload, 10) > 10000) {
      sessionStorage.setItem(chunkReloadKey, String(now));
      window.location.reload();
    }
  }
});

export default router;
